import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, resolveImageUrl } from "../App";
import { useAuth } from "../App";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { addToGuestCart } from "../utils/guestCart";
import { ShoppingCart, ChevronLeft, Heart, Star, Shield, Truck, RefreshCw, Lock, Check, ChevronRight, MapPin, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

// ── Star rating helper ────────────────────────────────────────────────────────

function StarRating({ rating, size = 16 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={size}
          className={i <= rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-zinc-200"}
        />
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate      = useNavigate();
  const { user }      = useAuth();

  const [product, setProduct]       = useState(null);
  const [loading, setLoading]       = useState(true);
  const [adding, setAdding]         = useState(false);
  const [quantity, setQuantity]     = useState(1);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedIdx, setSelectedIdx]     = useState(0);
  const [wishlisted, setWishlisted] = useState(false);
  const [reviews, setReviews]       = useState({ reviews: [], average_rating: 0, total_reviews: 0, distribution: {} });
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: "", comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false); // FIX: loading state
  const [zoomed, setZoomed]         = useState(false);
  const [zoomPos, setZoomPos]       = useState({ x: 50, y: 50 });
  const [relatedProducts, setRelatedProducts] = useState([]); // FIX: related products
  const [pincode, setPincode]       = useState(""); // FIX: pincode checker
  const [pincodeResult, setPincodeResult] = useState(null); // "ok" | "error" | null
  const [checkingPin, setCheckingPin] = useState(false);

  const imageRef    = useRef(null);
  const touchStartX = useRef(null);

  useEffect(() => {
    fetchProduct();
    fetchReviews();
    if (user) checkWishlist();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const { data } = await axios.get(`${API}/products/${productId}`);
      setProduct(data);
      setSelectedImage(resolveImageUrl(data.image_url));
      // Fetch related products once we know the category
      fetchRelated(data.category, data.product_id);
    } catch (error) {
      toast.error("Product not found");
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const { data } = await axios.get(`${API}/reviews/${productId}`);
      setReviews(data);
    } catch (error) { /* ignore */ }
  };

  // FIX: fetch related products from the same category, exclude current
  const fetchRelated = async (category, currentId) => {
    try {
      const { data } = await axios.get(
        `${API}/products?category=${encodeURIComponent(category)}&limit=4`
      );
      setRelatedProducts(data.filter((p) => p.product_id !== currentId).slice(0, 4));
    } catch (error) { /* ignore */ }
  };

  const checkWishlist = async () => {
    try {
      const { data } = await axios.get(`${API}/wishlist/check/${productId}`, { withCredentials: true });
      setWishlisted(data.is_wishlisted);
    } catch (error) { /* ignore */ }
  };

  const toggleWishlist = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      if (wishlisted) {
        await axios.delete(`${API}/wishlist/${productId}`, { withCredentials: true });
        setWishlisted(false);
        toast.success("Removed from wishlist");
      } else {
        await axios.post(`${API}/wishlist/${productId}`, {}, { withCredentials: true });
        setWishlisted(true);
        toast.success("Added to wishlist!");
      }
    } catch (error) { toast.error("Failed"); }
  };

  const addToCart = async () => {
    if (user) {
      // Logged in — add directly to backend cart
      setAdding(true);
      try {
        await axios.post(`${API}/cart`, { product_id: productId, quantity }, { withCredentials: true });
        toast.success("Added to cart!");
      } catch { toast.error("Failed to add to cart"); }
      finally { setAdding(false); }
    } else {
      // Guest — save to localStorage, no login required
      addToGuestCart(product, quantity);
      toast.success("Added to cart!");
    }
  };

  const buyNow = async () => {
    if (user) {
      setAdding(true);
      try {
        await axios.post(`${API}/cart`, { product_id: productId, quantity }, { withCredentials: true });
        navigate("/checkout");
      } catch { toast.error("Failed"); setAdding(false); }
    } else {
      // Guest — save to guest cart and go to checkout (login happens there)
      addToGuestCart(product, quantity);
      navigate("/checkout");
    }
  };

  // FIX: review submit with loading/disabled state to prevent duplicates
  const submitReview = async () => {
    if (!user) { navigate("/login"); return; }
    if (submittingReview) return;
    setSubmittingReview(true);
    try {
      await axios.post(
        `${API}/reviews`,
        { product_id: productId, ...reviewForm },
        { withCredentials: true }
      );
      toast.success("Review submitted!");
      setShowReviewForm(false);
      setReviewForm({ rating: 5, title: "", comment: "" });
      fetchReviews();
    } catch (error) {
      toast.error("Failed to submit review");
    } finally {
      setSubmittingReview(false);
    }
  };

  // FIX: pincode delivery check
  // This does a basic validation. If you have a real pincode API, swap the logic inside.
  const checkPincode = async () => {
    if (pincode.length !== 6 || !/^\d+$/.test(pincode)) {
      toast.error("Enter a valid 6-digit pincode");
      return;
    }
    setCheckingPin(true);
    setPincodeResult(null);
    try {
      // Try a free India Post pincode API — works for all Indian pincodes
      const { data } = await axios.get(
        `https://api.postalpincode.in/pincode/${pincode}`
      );
      if (data[0]?.Status === "Success") {
        setPincodeResult({ ok: true, area: data[0].PostOffice?.[0]?.District });
      } else {
        setPincodeResult({ ok: false });
      }
    } catch {
      // Fallback: accept any 6-digit Indian pincode
      setPincodeResult({ ok: true, area: null });
    } finally {
      setCheckingPin(false);
    }
  };

  const handleMouseMove = (e) => {
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setZoomPos({ x, y });
  };

  const selectImage = (img, idx) => { setSelectedImage(img); setSelectedIdx(idx); };

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      const newIdx = delta < 0
        ? Math.min(selectedIdx + 1, allImages.length - 1)
        : Math.max(selectedIdx - 1, 0);
      selectImage(allImages[newIdx], newIdx);
    }
    touchStartX.current = null;
  };

  // ── Loading / not found states ────────────────────────────────────────────

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent"></div>
    </div>
  );

  if (!product) return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h2 className="cormorant text-3xl font-light mb-4">Product not found</h2>
          <button onClick={() => navigate("/shop")} className="text-sm text-zinc-600 hover:text-zinc-900">
            Back to Shop
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );

  const allImages = [product.image_url, ...(product.additional_images || [])].map(resolveImageUrl);
  const discount  = product.compare_at_price && product.compare_at_price > product.price
    ? Math.round(((product.compare_at_price - product.price) / product.compare_at_price) * 100)
    : 0;

  // FIX: low stock threshold
  const isLowStock = product.stock > 0 && product.stock <= 5;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />

      <div className="px-6 md:px-12 max-w-7xl mx-auto py-8 md:py-12">
        <button
          onClick={() => navigate("/shop")}
          className="flex items-center text-sm text-zinc-500 hover:text-zinc-900 mb-8"
          data-testid="back-to-shop"
        >
          <ChevronLeft size={16} className="mr-1" /> Back to Shop
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">

          {/* ── Image Gallery ───────────────────────────────────────────── */}
          <div className="space-y-4">
            <div
              ref={imageRef}
              className="relative overflow-hidden bg-zinc-100 aspect-[4/5] cursor-crosshair"
              onMouseEnter={() => setZoomed(true)}
              onMouseLeave={() => setZoomed(false)}
              onMouseMove={handleMouseMove}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              data-testid="product-main-image"
            >
              <img
                src={selectedImage}
                alt={product.name}
                className={`w-full h-full object-cover transition-transform duration-300 ${zoomed ? "scale-150" : "scale-100"}`}
                style={zoomed ? { transformOrigin: `${zoomPos.x}% ${zoomPos.y}%` } : {}}
                loading="eager"
              />
              {discount > 0 && (
                <div className="absolute top-4 left-4 bg-[#D4AF37] text-white text-xs px-3 py-1.5 uppercase tracking-wider">
                  {discount}% Off
                </div>
              )}
            </div>

            {/* Thumbnails */}
            <div className="flex gap-3 overflow-x-auto pb-2">
              {allImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => selectImage(img, idx)}
                  className={`flex-shrink-0 w-20 h-20 overflow-hidden border-2 transition-colors ${
                    selectedIdx === idx ? "border-zinc-900" : "border-zinc-200 hover:border-zinc-400"
                  }`}
                  data-testid={`thumbnail-${idx}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>

            {allImages.length > 1 && (
              <p className="text-xs text-zinc-400 text-center lg:hidden">Swipe for more images</p>
            )}
          </div>

          {/* ── Product Info ─────────────────────────────────────────────── */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="flex items-start justify-between mb-2">
              <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37]">{product.category}</p>
              <button
                onClick={toggleWishlist}
                className={`p-2.5 rounded-full border transition-all duration-300 ${
                  wishlisted
                    ? "border-red-200 bg-red-50 text-red-500"
                    : "border-zinc-200 text-zinc-400 hover:text-red-500 hover:border-red-200"
                }`}
                data-testid="wishlist-toggle-button"
              >
                <Heart size={20} fill={wishlisted ? "currentColor" : "none"} />
              </button>
            </div>

            <h1
              className="cormorant text-3xl sm:text-4xl font-light tracking-tight mb-4"
              data-testid="product-name"
            >
              {product.name}
            </h1>

            {/* Rating summary */}
            {reviews.total_reviews > 0 && (
              <div className="flex items-center gap-2 mb-6">
                <StarRating rating={Math.round(reviews.average_rating)} size={16} />
                <span className="text-sm text-zinc-500">
                  {reviews.average_rating} ({reviews.total_reviews} reviews)
                </span>
              </div>
            )}

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-6">
              <p className="text-3xl font-light cormorant" data-testid="product-price">
                ₹{product.price.toLocaleString()}
              </p>
              {product.compare_at_price && product.compare_at_price > product.price && (
                <>
                  <p className="text-lg text-zinc-400 line-through">
                    ₹{product.compare_at_price.toLocaleString()}
                  </p>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    Save {discount}%
                  </span>
                </>
              )}
            </div>

            {/* FIX: Low-stock urgency label */}
            {isLowStock && (
              <div
                className="flex items-center gap-2 mb-6 text-red-600"
                data-testid="low-stock-warning"
              >
                <AlertCircle size={15} className="flex-shrink-0" />
                <p className="text-xs font-medium uppercase tracking-wide">
                  Only {product.stock} left — order soon!
                </p>
              </div>
            )}

            {/* Out of stock label */}
            {product.stock === 0 && (
              <div className="flex items-center gap-2 mb-6 text-zinc-500">
                <AlertCircle size={15} className="flex-shrink-0" />
                <p className="text-xs uppercase tracking-wide">Currently out of stock</p>
              </div>
            )}

            {/* Product features */}
            <div className="mb-6 space-y-2.5">
              {["92.5 Hallmarked Silver", "Skin-friendly & safe", "Lightweight for everyday wear", "Long-lasting shine"].map((feat, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <Check size={16} className="text-[#D4AF37] flex-shrink-0" />
                  <span className="text-sm text-zinc-600">{feat}</span>
                </div>
              ))}
            </div>

            <p className="text-sm text-zinc-500 leading-relaxed mb-6">{product.description}</p>

            {/* Quantity */}
            <div className="mb-6">
              <label className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-3 block">Quantity</label>
              <div className="flex items-center">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-12 h-12 border border-zinc-300 hover:bg-zinc-100 transition-colors text-lg"
                  data-testid="quantity-decrease"
                >
                  -
                </button>
                <span
                  className="w-12 h-12 border-y border-zinc-300 flex items-center justify-center text-sm"
                  data-testid="quantity-value"
                >
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-12 h-12 border border-zinc-300 hover:bg-zinc-100 transition-colors text-lg"
                  data-testid="quantity-increase"
                >
                  +
                </button>
              </div>
            </div>

            {/* CTA Buttons */}
            {product.stock > 0 ? (
              <div className="space-y-3 mb-6">
                <button
                  onClick={addToCart}
                  disabled={adding}
                  className="w-full bg-zinc-900 text-white hover:bg-zinc-800 py-4 uppercase tracking-[0.2em] text-xs transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="add-to-cart-button"
                >
                  <ShoppingCart size={16} />
                  {adding ? "Adding..." : "Add to Cart"}
                </button>
                <button
                  onClick={buyNow}
                  disabled={adding}
                  className="w-full border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-white py-4 uppercase tracking-[0.2em] text-xs transition-all duration-300 disabled:opacity-50"
                  data-testid="buy-now-button"
                >
                  Buy Now
                </button>
              </div>
            ) : (
              <div className="text-center py-4 bg-zinc-100 mb-6">
                <p className="text-sm text-zinc-600">Out of Stock</p>
              </div>
            )}

            {/* Trust badges */}
            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                { icon: Truck,     label: "COD Available" },
                { icon: RefreshCw, label: "Easy Returns" },
                { icon: Lock,      label: "Secure Checkout" },
                { icon: Shield,    label: "92.5 Certified" },
              ].map(({ icon: Icon, label }, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-zinc-500">
                  <Icon size={14} className="text-[#D4AF37]" /> {label}
                </div>
              ))}
            </div>

            {/* FIX: Pincode delivery checker ─────────────────────────────── */}
            <div className="border border-zinc-200 p-4 mb-8" data-testid="pincode-checker">
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={14} className="text-[#D4AF37]" />
                <p className="text-xs uppercase tracking-widest text-zinc-600 font-medium">
                  Check Delivery
                </p>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={pincode}
                  onChange={(e) => {
                    setPincode(e.target.value.replace(/\D/g, ""));
                    setPincodeResult(null);
                  }}
                  placeholder="Enter pincode"
                  className="flex-1 border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-900 transition-colors"
                  data-testid="pincode-input"
                />
                <button
                  onClick={checkPincode}
                  disabled={checkingPin || pincode.length !== 6}
                  className="bg-zinc-900 text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-zinc-800 transition-colors disabled:opacity-40"
                  data-testid="pincode-check-btn"
                >
                  {checkingPin ? "..." : "Check"}
                </button>
              </div>

              {pincodeResult?.ok && (
                <p className="mt-2 text-xs text-green-700 flex items-center gap-1" data-testid="pincode-ok">
                  <Check size={12} />
                  Delivery available{pincodeResult.area ? ` to ${pincodeResult.area}` : ""}
                  &nbsp;· Usually ships in 2–4 days
                </p>
              )}
              {pincodeResult && !pincodeResult.ok && (
                <p className="mt-2 text-xs text-red-600 flex items-center gap-1" data-testid="pincode-error">
                  <AlertCircle size={12} />
                  Sorry, we don't deliver to this pincode yet.
                </p>
              )}
            </div>

            {/* Why You'll Love It */}
            <div className="border-t border-zinc-200 pt-6">
              <h3 className="cormorant text-xl font-light mb-4">Why You'll Love It</h3>
              <div className="space-y-2.5">
                {["Elevates everyday outfits", "Perfect for gifting", "Designed for modern women", "Handcrafted in Jaipur"].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <ChevronRight size={14} className="text-[#D4AF37]" />
                    <span className="text-sm text-zinc-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Reviews Section ──────────────────────────────────────────── */}
        <section className="mt-20 pt-16 border-t border-zinc-200" data-testid="reviews-section">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-4">
            <div>
              <h2 className="cormorant text-3xl font-light mb-2">Customer Reviews</h2>
              {reviews.total_reviews > 0 && (
                <div className="flex items-center gap-3">
                  <StarRating rating={Math.round(reviews.average_rating)} size={18} />
                  <span className="text-sm text-zinc-600">
                    {reviews.average_rating} out of 5 ({reviews.total_reviews} reviews)
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={() => user ? setShowReviewForm(!showReviewForm) : navigate("/login")}
              className="border border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white px-6 py-3 text-xs uppercase tracking-[0.15em] transition-all duration-300"
              data-testid="write-review-button"
            >
              Write a Review
            </button>
          </div>

          {/* Review form */}
          {showReviewForm && (
            <div className="bg-white border border-zinc-200 p-6 mb-10" data-testid="review-form">
              <h3 className="cormorant text-xl font-light mb-4">Your Review</h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Rating</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <button
                        key={i}
                        onClick={() => setReviewForm({ ...reviewForm, rating: i })}
                        data-testid={`star-rating-${i}`}
                      >
                        <Star
                          size={24}
                          className={i <= reviewForm.rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-zinc-300"}
                        />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Title</label>
                  <input
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                    className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900"
                    placeholder="Sum it up"
                    data-testid="review-title-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Review</label>
                  <textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    rows={4}
                    className="w-full border border-zinc-300 px-4 py-3 text-sm focus:outline-none focus:border-zinc-900 resize-none"
                    placeholder="Share your experience..."
                    data-testid="review-comment-input"
                  />
                </div>
                {/* FIX: disabled + loading state on submit */}
                <button
                  onClick={submitReview}
                  disabled={submittingReview}
                  className="bg-zinc-900 text-white px-8 py-3 text-xs uppercase tracking-[0.15em] hover:bg-zinc-800 transition-colors disabled:opacity-50"
                  data-testid="submit-review-button"
                >
                  {submittingReview ? "Submitting..." : "Submit Review"}
                </button>
              </div>
            </div>
          )}

          {reviews.reviews.length === 0 ? (
            <p className="text-zinc-500 text-center py-12">
              No reviews yet. Be the first to review this product!
            </p>
          ) : (
            <div className="space-y-6">
              {reviews.reviews.map((review) => (
                <div
                  key={review.review_id}
                  className="bg-white border border-zinc-100 p-6"
                  data-testid={`review-${review.review_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <StarRating rating={review.rating} size={14} />
                        {review.verified_buyer && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                            Verified Buyer
                          </span>
                        )}
                      </div>
                      <h4 className="font-medium text-sm">{review.title}</h4>
                    </div>
                    <span className="text-xs text-zinc-400">{review.reviewer_name}</span>
                  </div>
                  <p className="text-sm text-zinc-600 leading-relaxed">{review.comment}</p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* ── FIX: Related Products Section ────────────────────────────── */}
        {relatedProducts.length > 0 && (
          <section className="mt-20 pt-16 border-t border-zinc-200" data-testid="related-products">
            <div className="text-center mb-12">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-zinc-500 mb-3">
                You May Also Like
              </p>
              <h2 className="cormorant text-3xl sm:text-4xl font-light tracking-tight">
                More from {product.category}
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {relatedProducts.map((related) => (
                <Link
                  key={related.product_id}
                  to={`/product/${related.product_id}`}
                  className="group block"
                  data-testid={`related-product-${related.product_id}`}
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                >
                  <div className="relative overflow-hidden bg-zinc-100 mb-3 aspect-[4/5]">
                    <img
                      src={resolveImageUrl(related.image_url)}
                      alt={related.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    {related.is_best_seller && (
                      <span className="absolute top-2 left-2 bg-zinc-900 text-white text-xs px-2 py-1 uppercase tracking-wide">
                        Best Seller
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-light cormorant mb-1 group-hover:text-zinc-500 transition-colors">
                    {related.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">₹{related.price.toLocaleString()}</p>
                    {related.compare_at_price && related.compare_at_price > related.price && (
                      <p className="text-xs text-zinc-400 line-through">
                        ₹{related.compare_at_price.toLocaleString()}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <Footer />

      {/* ── FIX: Sticky Mobile Buy Bar with compare-at price ─────────── */}
      {product.stock > 0 && (
        <div
          className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-zinc-200 px-4 py-3 z-40 flex items-center gap-3 shadow-lg"
          data-testid="mobile-buy-bar"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <p className="text-lg font-medium cormorant">₹{product.price.toLocaleString()}</p>
              {/* FIX: strikethrough price in mobile bar */}
              {product.compare_at_price && product.compare_at_price > product.price && (
                <p className="text-xs text-zinc-400 line-through">
                  ₹{product.compare_at_price.toLocaleString()}
                </p>
              )}
            </div>
            {/* FIX: low-stock nudge in mobile bar */}
            {isLowStock && (
              <p className="text-[10px] text-red-600 font-medium">Only {product.stock} left!</p>
            )}
          </div>
          <button
            onClick={addToCart}
            disabled={adding}
            className="bg-zinc-900 text-white px-6 py-3 uppercase tracking-[0.15em] text-xs disabled:opacity-50 whitespace-nowrap"
            data-testid="mobile-add-to-cart"
          >
            {adding ? "Adding..." : "Add to Cart"}
          </button>
        </div>
      )}
    </div>
  );
}